// src/components/PhotoUploader.jsx
//
// Управление массивом фото в форме товара.
// - Загрузка нескольких файлов через input[multiple]
// - Сетка превьюшек 3 в ряд
// - Перетаскивание для смены порядка (через HTML5 drag/drop) — десктоп.
//   На мобиле — кнопки ↑↓ для перемещения. Простой подход без сторонних либ.
// - Удаление через крестик
// - Первая фотка — обложка, помечена меткой
//
// Props:
//   photos       — array of URLs
//   onChange     — callback(newPhotos)
//   onError      — callback(message)
//   uploadFn     — async (files: File[]) => { urls: string[] }

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

export default function PhotoUploader({ photos = [], onChange, onError, uploadFn }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Камера уместна только на устройстве с камерой (телефон/планшет).
  // На десктопе кнопку «Снять» не показываем — там её нажатие бессмысленно.
  const [hasCamera, setHasCamera] = useState(false);

  useEffect(() => {
    const coarse = typeof window !== 'undefined'
      && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const touch = typeof navigator !== 'undefined'
      && (navigator.maxTouchPoints || 0) > 0;
    setHasCamera(!!(coarse || touch));
  }, []);

  const pickFiles = () => fileRef.current?.click();
  const openCamera = () => camRef.current?.click();

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const { urls } = await uploadFn(files);
      if (Array.isArray(urls) && urls.length > 0) {
        onChange([...photos, ...urls]);
      }
    } catch (err) {
      onError?.(err.message);
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeAt = (idx) => {
    onChange(photos.filter((_, i) => i !== idx));
  };

  const move = (from, to) => {
    if (to < 0 || to >= photos.length) return;
    const next = [...photos];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div className="input-group">
      <label>{t('products.photos')}</label>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFile}
        style={{ display: 'none' }}
      />

      {/* Отдельный input для съёмки: capture просит систему открыть камеру,
          а не галерею. Работает и в приложении (Android WebView), и в
          мобильном браузере. Один кадр за раз — так работает камера. */}
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile}
        style={{ display: 'none' }}
      />

      {photos.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8, marginBottom: 8,
        }}>
          {photos.map((url, idx) => (
            <div
              key={url + idx}
              style={{
                position: 'relative',
                aspectRatio: '1', borderRadius: 'var(--r-sm)',
                overflow: 'hidden', background: 'var(--bg-subtle)',
                border: idx === 0 ? '2px solid var(--brand-primary)' : '1px solid var(--border)',
              }}
            >
              <img
                src={url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />

              {/* Метка обложки на первом фото */}
              {idx === 0 && (
                <div style={{
                  position: 'absolute', top: 4, left: 4,
                  background: 'var(--brand-primary)', color: '#fff',
                  fontSize: 9, fontWeight: 700,
                  padding: '2px 6px', borderRadius: 6,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {t('products.mainPhoto')}
                </div>
              )}

              {/* Кнопки перестановки */}
              <div style={{
                position: 'absolute', bottom: 4, left: 4,
                display: 'flex', gap: 2,
              }}>
                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => move(idx, idx - 1)}
                    style={iconBtn}
                    aria-label="Move left"
                  >
                    <Icon name="chevronLeft" size={12} stroke={2.5} />
                  </button>
                )}
                {idx < photos.length - 1 && (
                  <button
                    type="button"
                    onClick={() => move(idx, idx + 1)}
                    style={iconBtn}
                    aria-label="Move right"
                  >
                    <Icon name="chevronRight" size={12} stroke={2.5} />
                  </button>
                )}
              </div>

              {/* Удалить */}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                style={{
                  ...iconBtn,
                  position: 'absolute', top: 4, right: 4,
                  background: 'rgba(0,0,0,0.65)',
                }}
                aria-label="Remove"
              >
                <Icon name="x" size={12} stroke={2.5} />
              </button>
            </div>
          ))}

          {/* Снять камерой — ещё одна ячейка */}
          {hasCamera && (
            <button
              type="button"
              onClick={openCamera}
              disabled={uploading}
              style={{
                aspectRatio: '1', borderRadius: 'var(--r-sm)',
                border: '2px dashed var(--brand-primary)',
                background: 'var(--brand-primary-soft, var(--bg-subtle))', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--brand-primary)',
              }}
              aria-label={t('products.takePhoto', 'Сделать фото')}
            >
              {uploading ? <div className="spinner" /> : <Icon name="camera" size={20} stroke={2} />}
            </button>
          )}

          {/* Кнопка-плюс (галерея) в сетке как ещё одна ячейка */}
          <button
            type="button"
            onClick={pickFiles}
            disabled={uploading}
            style={{
              aspectRatio: '1', borderRadius: 'var(--r-sm)',
              border: '2px dashed var(--border)',
              background: 'var(--bg-subtle)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--fg-muted)',
            }}
            aria-label={t('products.fromGallery', 'Из галереи')}
          >
            {uploading ? <div className="spinner" /> : <Icon name="plus" size={20} stroke={2} />}
          </button>
        </div>
      )}

      {photos.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {hasCamera && (
            <button
              type="button"
              onClick={openCamera}
              disabled={uploading}
              className="btn btn-primary"
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              {uploading ? <div className="spinner" /> : <Icon name="camera" size={18} stroke={2} />}
              {t('products.takePhoto', 'Сделать фото')}
            </button>
          )}
          <button
            type="button"
            onClick={pickFiles}
            disabled={uploading}
            style={{
              width: '100%', aspectRatio: hasCamera ? 'auto' : '4/3',
              minHeight: hasCamera ? 56 : undefined,
              background: 'var(--bg-subtle)', border: '2px dashed var(--border)',
              borderRadius: 'var(--r-md)', cursor: 'pointer',
              display: 'flex', flexDirection: hasCamera ? 'row' : 'column',
              alignItems: 'center', justifyContent: 'center',
              color: 'var(--fg-faint)', gap: hasCamera ? 8 : 6,
            }}
          >
            {uploading ? (
              <div className="spinner" />
            ) : (
              <>
                <Icon name="image" size={hasCamera ? 18 : 28} stroke={hasCamera ? 2 : 1.5} />
                <div style={{ fontSize: 13 }}>
                  {hasCamera ? t('products.fromGallery', 'Из галереи') : t('products.addPhotos')}
                </div>
              </>
            )}
          </button>
        </div>
      )}

      {photos.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
          {t('products.reorderHint')}
        </p>
      )}
    </div>
  );
}

const iconBtn = {
  width: 22, height: 22, borderRadius: '50%',
  background: 'rgba(0,0,0,0.55)', border: 'none',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#fff', cursor: 'pointer',
  flexShrink: 0,
};
